import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { Menu } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import { openGlobalSearch } from 'app/components/global-search/globalSearchBus'
import { Button } from 'app/components/ui/button'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useUser, useWorkspace } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'

type Props = {
  onOpenNav: () => void
  navOpen: boolean
}

/**
 * Mobile-only top bar (< md). Hosts the hamburger that opens the nav drawer and
 * where you currently are — the workspace or organization name, with a small
 * context label beneath it. Deliberately minimal — page titles/actions stay in
 * PageHeader. Respects the iOS safe-area inset at the top.
 */
export const MobileTopBar = observer(function MobileTopBar({ onOpenNav, navOpen }: Props): React.ReactElement {
  const workspace = useWorkspace()
  const user = useUser()
  const globalSearchEnabled = useFeatureToggle(`global-search`)

  // In a specific workspace we show its name; at the org level (no workspace
  // selected) we show the organization name instead. The sub-label tells you
  // which of the two you're looking at.
  const inWorkspace = Boolean(workspace?.name)
  const name = inWorkspace ? workspace!.name : isSMCOrg(user?.orgId) ? `SMC` : `Trackable`
  const contextLabel = inWorkspace ? `Workspace` : `Organization`

  return (
    <header className="flex h-[calc(3.5rem+env(safe-area-inset-top))] shrink-0 items-center gap-2 border-b border-default bg-surface-subtle px-3 pt-[env(safe-area-inset-top)]">
      <button
        type="button"
        onClick={onOpenNav}
        aria-label="Open navigation"
        aria-expanded={navOpen}
        aria-controls="app-nav-drawer"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-surface-muted"
      >
        <Menu className="h-6 w-6 text-default" aria-hidden="true" />
      </button>
      <div className="flex min-w-0 flex-col justify-center">
        <span className="min-w-0 truncate text-base font-semibold leading-tight text-strong">{name}</span>
        <span className="text-[11px] font-medium leading-tight text-subtle">{contextLabel}</span>
      </div>
      {globalSearchEnabled && (
        <Button
          variant="icon"
          size="icon"
          onClick={openGlobalSearch}
          aria-label="Search"
          className="ml-auto text-default"
        >
          {/* Button's base style forces `size-4` on any svg without a `size-` class of its own,
              so the icon has to carry the class to render at 22px next to the 24px hamburger. */}
          <MagnifyingGlassIcon className="size-[22px]" />
        </Button>
      )}
    </header>
  )
})
